import type { PageMeta, ThemeColors } from "@emkit/builder-core";
import { useEditor } from "../store.js";
import { ColorField, NumberField, TextField } from "./inspectors/fields.js";
import { MediaField } from "./inspectors/MediaField.js";

export function PageSettings() {
  const isOpen = useEditor((s) => s.isPageSettingsOpen);
  const close = useEditor((s) => s.closePageSettings);
  const page = useEditor((s) => s.page);
  const update = useEditor((s) => s.updatePageMeta);
  const updateTheme = useEditor((s) => s.updatePageTheme);

  if (!isOpen) return null;

  const meta = page.meta ?? {};
  const og = meta.og ?? {};
  const theme = page.theme ?? {};
  const colors: ThemeColors = theme.colors ?? {};
  const setColor = (name: keyof ThemeColors, v: string) => {
    updateTheme({ colors: { [name]: v || undefined } });
  };

  const setMeta = (patch: Partial<PageMeta>) => {
    update({ meta: { ...meta, ...patch } });
  };

  const setOg = (patch: Partial<NonNullable<PageMeta["og"]>>) => {
    update({ meta: { ...meta, og: { ...og, ...patch } } });
  };

  return (
    <div
      className="emke-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Page settings"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="emke-modal" style={{ maxWidth: 560 }}>
        <header className="emke-modal-header">
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-dim)",
              }}
            >
              Settings
            </div>
            <h2 style={{ margin: "4px 0 0 0", fontSize: 18 }}>Page settings</h2>
          </div>
          <button
            type="button"
            className="emke-btn"
            onClick={close}
            aria-label="Close page settings"
          >
            ✕
          </button>
        </header>

        <div className="emke-modal-body" style={{ padding: "8px 0 4px" }}>
          <TextField
            label="Title"
            value={page.title}
            onChange={(v) => update({ title: v })}
            placeholder="Untitled"
          />
          <TextField
            label="Slug"
            value={page.slug}
            onChange={(v) => update({ slug: v })}
            placeholder="/"
          />
          <TextField
            label="Meta description"
            value={meta.description ?? ""}
            onChange={(v) => setMeta({ description: v })}
            placeholder="What this page is about (≤160 chars)"
            multiline
          />
          <TextField
            label="Robots"
            value={meta.robots ?? ""}
            onChange={(v) => setMeta({ robots: v })}
            placeholder="index, follow"
          />

          <div
            style={{
              padding: "12px 16px 4px",
              fontSize: 11,
              color: "var(--fg-dim)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Open Graph (social preview)
          </div>
          <TextField
            label="OG title"
            value={og.title ?? ""}
            onChange={(v) => setOg({ title: v })}
            placeholder="Falls back to page title"
          />
          <TextField
            label="OG description"
            value={og.description ?? ""}
            onChange={(v) => setOg({ description: v })}
            placeholder="Falls back to meta description"
            multiline
          />
          <MediaField
            label="OG image URL"
            value={og.image ?? ""}
            onChange={(v) => setOg({ image: v })}
            placeholder="https://example.com/og.png (1200×630)"
          />

          <div
            style={{
              padding: "12px 16px 4px",
              fontSize: 11,
              color: "var(--fg-dim)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Theme — applied to the whole page
          </div>
          <div style={{ padding: "0 16px 4px", fontSize: 12, color: "var(--fg-muted)" }}>
            Reference colors from any widget with <code>$brand</code>,{" "}
            <code>$accent</code>, etc.
          </div>
          <ColorField
            label="Brand"
            value={colors.brand ?? ""}
            onChange={(v) => setColor("brand", v)}
          />
          <ColorField
            label="Accent"
            value={colors.accent ?? ""}
            onChange={(v) => setColor("accent", v)}
          />
          <ColorField
            label="Background"
            value={colors.background ?? ""}
            onChange={(v) => setColor("background", v)}
          />
          <ColorField
            label="Foreground (text)"
            value={colors.foreground ?? ""}
            onChange={(v) => setColor("foreground", v)}
          />
          <ColorField
            label="Muted"
            value={colors.muted ?? ""}
            onChange={(v) => setColor("muted", v)}
          />
          <ColorField
            label="Border"
            value={colors.border ?? ""}
            onChange={(v) => setColor("border", v)}
          />
          <TextField
            label="Body font-family"
            value={theme.fontFamily ?? ""}
            onChange={(v) => updateTheme({ fontFamily: v })}
            placeholder='Inter, system-ui, "Helvetica Neue", sans-serif'
          />
          <TextField
            label="Heading font-family (optional)"
            value={theme.headingFontFamily ?? ""}
            onChange={(v) => updateTheme({ headingFontFamily: v })}
            placeholder="Falls back to body font"
          />
          <NumberField
            label="Base radius (px)"
            value={theme.baseRadius ?? 0}
            min={0}
            max={64}
            step={1}
            onChange={(v) => updateTheme({ baseRadius: v })}
          />
        </div>

        <footer className="emke-modal-footer">
          <span style={{ color: "var(--fg-dim)", fontSize: 12 }}>
            Changes apply on the next Export HTML. Undo (Ctrl+Z) reverts.
          </span>
        </footer>
      </div>
    </div>
  );
}
